import { IPromise } from 'angular'
import find from 'lodash/find'
import each from 'lodash/each'
import { vn, add, subtract, isFinite } from 'vend-number'
import type { VdToastNotificationService } from '../../../vend-ui/types'

import { EVENT, FEATURE_USE_2026_04_INVENTORY_API } from '../../../constants'
import { $q } from '../../../ng-compat/imports'
import {
  getInventory,
  getInventoryLegacy,
} from '../../product/products-resource/get-inventory'
import { LineItem } from '../../../models/line-item'
import { RegisterManager } from '../../../managers/register-manager'
import { WebRegisterStatusManager } from '../../../managers/web-register-status-manager'
import { EventEmitter } from '../../../util/event-emitter'
import { PosStateManager } from '../../../managers/pos-state-manager'
import { isGiftCard } from '../../../functions/lineItem'
import { getFeatureFromCache } from '../../../util/feature-cache'

type Events = {
  [EVENT.LINE_ITEM_UPDATE_VALIDATION]: []
}

interface InventoryItem {
  productId: string
  inventoryLevel: string | number
  currentInventoryInSale: string | number
  validateInventoryLevel: boolean
}

type DisabledValidation = Set<string>

/**
 * Service that holds the sale inventory data
 *
 * @class SaleInventoryData
 */
export class SaleInventoryData extends EventEmitter<Events> {
  public saleInventoryData: InventoryItem[] = []
  public disabledValidation: DisabledValidation = new Set()
  public isLoading: boolean = false
  constructor(
    private vdToastNotificationService: VdToastNotificationService,
    private posStateManager: PosStateManager,
    private registerManager: RegisterManager,
    private webRegisterStatusManager: WebRegisterStatusManager
  ) {
    'ngInject'
    super()
  }

  /**
   * Get the inventory data of the given product.
   *
   * @method lookupInventoryData
   *
   * @param  {LineItem} lineItem
   *         The lineItem include the product (non-variant or parent product) to get the inventory data for
   *
   * @return {Promise} Resolved with looking up the inventory data for a given product
   */
  public lookupInventoryData(lineItem: Readonly<LineItem>) {
    const product = lineItem.product

    if (
      this.posStateManager.getTrainingModeEnabled() ||
      !this.webRegisterStatusManager.isOnline() ||
      !this.registerManager.isLowInventoryWarningEnabled() ||
      !product.has_inventory ||
      isGiftCard(lineItem.product) ||
      lineItem.confirmed ||
      this._isReturn(lineItem)
    ) {
      return Promise.resolve()
    }
    this.isLoading = true

    let lookupInventoryDataPromise = $q.resolve()

    if (this._isInSaleInventoryData(lineItem)) {
      this.addRemoveQuantity(lineItem)
    } else {
      const activeOutlet = this.registerManager.getActiveOutlet()
      const outletId = activeOutlet.id

      const useNewLevelsApi = getFeatureFromCache(
        FEATURE_USE_2026_04_INVENTORY_API
      )

      const inventoryPromise = useNewLevelsApi
        ? getInventory([product.id], {
            include_variants: false,
            location_ids: [outletId],
          }).then(inventoryData => {
            if (inventoryData && inventoryData.length > 0) {
              const inventoryOutletData = find(inventoryData, {
                location_id: outletId,
              })

              if (inventoryOutletData) {
                const item = {
                  productId: inventoryOutletData.product_id,
                  inventoryLevel: inventoryOutletData.current_inventory_level,
                  currentInventoryInSale: lineItem.quantity,
                  validateInventoryLevel: true,
                }

                this.saleInventoryData.push(item)
              }
            } else {
              // If product has inventory tracking, we intialise an item in local storage with zero inventory level.
              // This is because the outlet does not yet have inventory data
              if (lineItem.product.has_inventory) {
                const item = {
                  productId: product.id,
                  inventoryLevel: 0,
                  currentInventoryInSale: lineItem.quantity,
                  validateInventoryLevel: true,
                }
                this.saleInventoryData.push(item)
              }
            }
          })
        : getInventoryLegacy([product.id], {
            include_variants: false,
            location_ids: [outletId],
          }).then(inventoryData => {
            if (inventoryData && inventoryData.length > 0) {
              const inventoryOutletData = find(inventoryData, {
                location_id: outletId,
              })

              if (inventoryOutletData) {
                const item = {
                  productId: inventoryOutletData.product_id,
                  inventoryLevel: add(
                    inventoryOutletData.levels.producible,
                    inventoryOutletData.levels.on_hand
                  ),
                  currentInventoryInSale: lineItem.quantity,
                  validateInventoryLevel: true,
                }

                this.saleInventoryData.push(item)
              }
            } else {
              // If product has inventory tracking, we intialise an item in local storage with zero inventory level.
              // This is because the outlet does not yet have inventory data
              if (lineItem.product.has_inventory) {
                const item = {
                  productId: product.id,
                  inventoryLevel: 0,
                  currentInventoryInSale: lineItem.quantity,
                  validateInventoryLevel: true,
                }
                this.saleInventoryData.push(item)
              }
            }
          })

      lookupInventoryDataPromise = inventoryPromise.catch(() => {
        return this.vdToastNotificationService.negative({
          message:
            'Failed to retrieve inventory levels, please refresh to try again.',
        })
      })
    }

    return lookupInventoryDataPromise.finally(() => {
      this.isLoading = false
      this.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
    })
  }

  /**
   * Disable inventory validation for a given lineItem product type
   *
   * @method disableInventoryValidation
   *
   * @param {LineItem} lineItem
   */
  public disableInventoryValidation(lineItem: LineItem) {
    const inventoryItem = this.getInventoryData(lineItem)

    if (!inventoryItem) {
      return
    }

    inventoryItem.validateInventoryLevel = false
    this.disabledValidation.add(lineItem.localId)

    this.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
  }

  /**
   * Re-enable inventory validation for a given lineItem product type
   *
   * @method enableInventoryValidation
   *
   * @param {LineItem} lineItem
   */
  public enableInventoryValidation(lineItem: LineItem) {
    const inventoryItem = this.getInventoryData(lineItem)

    if (!inventoryItem) {
      return
    }

    inventoryItem.validateInventoryLevel = true
    this.disabledValidation = new Set(
      Array.from(this.disabledValidation).filter(
        localId => localId !== lineItem.localId
      )
    )

    this.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
  }

  /**
   * Get the inventory data for a given lineItem product
   *
   * @method getInventoryData
   *
   * @param {LineItem} lineItem
   *
   * @return {Object} An object representing the inventory data for a given lineItem
   */
  public getInventoryData(lineItem: LineItem) {
    const inventoryItem = find(this.saleInventoryData, inventoryItem => {
      return inventoryItem.productId === lineItem.product.id
    })

    return inventoryItem
  }

  /**
   * Determine if we should validate inventory for a given lineItem
   *
   * @method shouldValidateInventory
   *
   * @param {LineItem} lineItem
   *
   * @return {Boolean} True if we can evaluate the inventory for a given lineItem, false otherwise
   */
  public shouldValidateInventory(lineItem: LineItem) {
    const inventoryItem = this.getInventoryData(lineItem)

    if (!inventoryItem) {
      return false
    }

    const notDisabled =
      inventoryItem.validateInventoryLevel ||
      !this.disabledValidation.has(lineItem.localId)

    const noReturnOrConfirmed = Boolean(
      !lineItem.confirmed && !this._isReturn(lineItem)
    )

    return Boolean(
      notDisabled && noReturnOrConfirmed && !isGiftCard(lineItem.product)
    )
  }

  /**
   * Add / Remove current inventory in sale quantity for a given lineItem
   * Note that this will only update the quantity of for the inventory item in the sale, the actual inventory object
   * is not removed. This allows us to save on a request to find out the inventory for an item if its used again during
   * the sale
   *
   * @method addRemoveQuantity
   *
   * @param {LineItem} lineItem
   *
   * @param {Object} [options]
   *
   *        @param {Boolean} [options.onRemove]
   *               True if the given quantity change was initiated by removing a lineItem
   *
   *        @param {Boolean} [options.hasLineItems]
   *               True if the given sale has lineItems left, false otherwise
   *
   */
  public addRemoveQuantity(
    lineItem: LineItem,
    options: {
      onRemove?: boolean
      hasLineItems?: boolean
    } = {}
  ) {
    const inventoryItem = this.getInventoryData(lineItem)

    if (!inventoryItem) {
      return
    }

    if (options.onRemove) {
      inventoryItem.currentInventoryInSale = subtract(
        inventoryItem.currentInventoryInSale,
        lineItem.quantity
      )

      if (options.hasLineItems) {
        this.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
      }

      return
    }

    inventoryItem.currentInventoryInSale = add(
      inventoryItem.currentInventoryInSale,
      lineItem.quantity
    )

    // Remove the lineItem from the disabledValidation set
    this.disabledValidation = new Set(
      Array.from(this.disabledValidation).filter(
        localId => localId !== lineItem.localId
      )
    )
  }

  /**
   * Re-evaluate and update the currentInventoryInSale for each inventory item
   *
   * @method reEvaluateQuantity
   *
   * @param {Array} lineItems
   *
   * @return {Promise} Resolved with the update of the current inventory in sale quantities
   */
  public reEvaluateQuantity(lineItems: LineItem[]) {
    this._resetCurrentInventoryInSaleData()

    const promiseQueue: Array<IPromise<void>> = []

    each(lineItems, lineItem => {
      if (lineItem.confirmed || this._isReturn(lineItem)) {
        return
      }

      const inventoryItem = this.getInventoryData(lineItem)

      if (!inventoryItem) {
        promiseQueue.push(this.lookupInventoryData(lineItem))
      } else {
        inventoryItem.currentInventoryInSale = add(
          inventoryItem.currentInventoryInSale,
          lineItem.quantity
        )
      }
    })

    return $q
      .all(promiseQueue)
      .then(() => this.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION))
  }

  /**
   * Determine if the current lineItem product has inventory available
   *
   * @method hasInventory
   *
   * @param {LineItem} lineItem
   *
   * @return {Boolean} True if the given lineItem product has inventory available, false if there is no inventory available or it is a non-inventory item
   */
  public hasInventory(lineItem: LineItem) {
    const inventoryItem = this.getInventoryData(lineItem)

    if (!inventoryItem) {
      return false
    }

    // Set totalQuantity to zero when checking onRemove lineItem
    const totalQuantity = vn(inventoryItem.currentInventoryInSale).eq(0)
      ? 0
      : subtract(
          inventoryItem.inventoryLevel,
          inventoryItem.currentInventoryInSale
        )

    return Boolean(!vn(totalQuantity).lt(0))
  }

  /**
   * Reset the saleInventoryData array
   *
   * @method reset
   */
  public reset() {
    this.saleInventoryData = []
    this.disabledValidation.clear()
  }

  /**
   * Reset the current in sale inventory quantity
   *
   * @private
   *
   * @method _resetCurrentInventoryInSaleData
   */
  private _resetCurrentInventoryInSaleData() {
    each(this.saleInventoryData, inventoryItem => {
      inventoryItem.currentInventoryInSale = 0
    })
  }

  /**
   * Deteremine if the current inventory product type has already been added
   *
   * @private
   *
   * @method _isInSaleInventoryData
   *
   * @param {LineItem} lineItem
   *
   * @return {Boolean} True if the given inventory type has already been added, false otherwise
   */
  private _isInSaleInventoryData(lineItem: LineItem) {
    const inventoryItem = this.getInventoryData(lineItem)

    return Boolean(inventoryItem)
  }

  /**
   * Determine if the lineItem is a return
   * For the purposes of inventory any lineItem with a negative quantity will be considered a return
   *
   * @private
   *
   * @method _isReturn
   *
   * @param {lineItem} lineItem
   *
   * @return {Boolean} True when the lineItem is a return, false otherwise
   */
  private _isReturn(lineItem: LineItem) {
    return isFinite(lineItem.quantity) && Boolean(vn(lineItem.quantity).lt(0))
  }
}
